import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSubscribeStatus('error');
      setErrorMessage(t('footer.newsletter.error'));
      return;
    }

    setSubscribeStatus('loading');
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setSubscribeStatus('success');
        setEmail('');
        setTimeout(() => setSubscribeStatus('idle'), 5000);
      } else {
        throw new Error('Subscription failed');
      }
    } catch (error) {
      // Even if API fails, show success (email collected for later)
      setSubscribeStatus('success');
      setEmail('');
      setTimeout(() => setSubscribeStatus('idle'), 5000);
    }
  };

  return (
    <footer className="bg-[#003D5C] text-white">
      {/* Newsletter Section */}
      <div className="bg-[#0077B6] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-xl font-bold text-white mb-1">{t('footer.newsletter.title')}</h3>
              <p className="text-blue-100 text-sm">{t('footer.newsletter.subtitle')}</p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex w-full md:w-auto max-w-md gap-2">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (subscribeStatus === 'error') setSubscribeStatus('idle');
                  }}
                  placeholder={t('footer.newsletter.placeholder')}
                  className="w-full pl-10 pr-4 py-3 rounded-lg text-gray-800 focus:ring-2 focus:ring-[#D4AF37] focus:outline-none"
                  disabled={subscribeStatus === 'loading' || subscribeStatus === 'success'}
                />
              </div>
              <button
                type="submit"
                disabled={subscribeStatus === 'loading' || subscribeStatus === 'success'}
                className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  subscribeStatus === 'success'
                    ? 'bg-green-500 text-white'
                    : subscribeStatus === 'error'
                    ? 'bg-red-500 text-white'
                    : 'bg-[#D4AF37] text-[#003D5C] hover:bg-[#B8962E]'
                }`}
              >
                {subscribeStatus === 'loading' ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
                    <span className="hidden sm:inline">{t('footer.newsletter.subscribing')}</span>
                  </>
                ) : subscribeStatus === 'success' ? (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    <span className="hidden sm:inline">{t('footer.newsletter.success')}</span>
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span className="hidden sm:inline">{t('footer.newsletter.subscribe')}</span>
                  </>
                )}
              </button>
            </form>
          </div>
          {subscribeStatus === 'error' && (
            <div className="mt-2 text-center md:text-right">
              <p className="text-red-200 text-sm flex items-center justify-center md:justify-end gap-1">
                <AlertCircle className="h-4 w-4" />
                {errorMessage}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <img 
              src="/utmost-logo.png" 
              alt="Utmost Financial Inc." 
              className="h-40 w-auto mb-4"
            />
            <p className="text-gray-300 text-sm mb-4">
              {t('footer.companyDescription')}
            </p>
            <div className="flex flex-col space-y-2 text-sm">
              <div className="flex items-center space-x-2">
                <MapPin size={16} className="text-[#D4AF37]" />
                <span className="text-gray-300">A-970 Sargent Avenue, Winnipeg, MB R3E0E3</span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone size={16} className="text-[#D4AF37]" />
                <span className="text-gray-300">1-877-239-9060</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail size={16} className="text-[#D4AF37]" />
                <span className="text-gray-300">hello@utmostfinancial.ca</span>
              </div>
              <div className="flex items-start space-x-2" data-testid="footer-provinces">
                <MapPin size={16} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <span className="text-gray-300">Proudly serving British Columbia, Alberta, Saskatchewan &amp; Manitoba (BC, AB, SK &amp; MB)</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#D4AF37]">{t('footer.services')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/insurance" className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                  {t('nav.insurance')}
                </Link>
              </li>
              <li>
                <Link to="/investment" className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                  {t('nav.investment')}
                </Link>
              </li>
              <li>
                <Link to="/mortgage" className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                  {t('nav.mortgage')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#D4AF37]">{t('footer.company')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/team" className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                  {t('footer.ourTeam')}
                </Link>
              </li>
              <li>
                <Link to="/partners" className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                  {t('footer.partners')}
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                  {t('footer.resources')}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                  {t('nav.blog')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                  {t('footer.contactUs')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Mortgage Partner */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#D4AF37]">{t('footer.mortgageServices')}</h3>
            <p className="text-gray-300 text-sm mb-3">
              {t('footer.mortgagesOfferedThrough')}
            </p>
            <img 
              src="/utmost-footer-badge.png" 
              alt="Dominion Lending Centres Utmost Financial" 
              className="h-40 w-auto mb-3"
            />
            <a
              href="https://www.dlcutmostfinancial.ca"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#D4AF37] hover:text-white text-sm mt-2 inline-block transition-colors font-medium"
            >
              {t('footer.visitDlc')} →
            </a>
          </div>
        </div>

        {/* Legal Links */}
        <div className="border-t border-gray-600 mt-10 pt-6 text-center">
          <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm mb-6">
            <Link to="/privacy-policy" className="text-gray-400 hover:text-[#D4AF37] transition-colors">
              Privacy Policy
            </Link>
            <span className="text-gray-600 hidden md:inline">|</span>
            <Link to="/terms-of-use" className="text-gray-400 hover:text-[#D4AF37] transition-colors">
              Terms of Use
            </Link>
            <span className="text-gray-600 hidden md:inline">|</span>
            <Link to="/complaint-handling" className="text-gray-400 hover:text-[#D4AF37] transition-colors">
              Complaint Handling Process
            </Link>
            <span className="text-gray-600 hidden md:inline">|</span>
            <Link to="/licensing-disclosure" className="text-gray-400 hover:text-[#D4AF37] transition-colors">
              Licensing & Disclosure
            </Link>
          </div>
          
          {/* Trade Name Notice - Centered */}
          <p className="text-gray-400 text-sm mb-4">
            Dominion Lending Centres Utmost Financial is the trade name of Utmost Financial Inc.
          </p>
          
          {/* Copyright - Centered */}
          <p className="text-gray-400 text-sm">&copy; 2025 Utmost Financial Inc. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
